import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Slider, TextInput } from '@tcc/crate';

import { getCurrency } from 'user/userSelectors';
import { getPlantingPlanParams } from 'planting-plan-form/selectors';
import { getProductValidationValues } from 'user/selectors';
import { getActiveScenario } from 'advanced/scenarios/scenarioSelectors';
import { validateAdvancedPlantingPlan } from 'current/validators';
import { loadNewAdvancedScript } from 'scripts';
import { useDebounce } from 'util/hooks/useDebounce';
import { formatNumberRoundDownToNearest, formatNumberRoundUpToNearest } from 'util/formatters/numberFormatter';
import { updatePlantingPlanParams } from 'planting-plan-form';
import { RATIOS } from 'constants/advanced';
import { getActiveCropId } from 'current/currentSelectors';

/**
  Gets the slider marker position ensuring that it is within
  the limits. Hank can give a seed rate recommendation outside
  of the limits that are valid for the user when a rate is input 
  manually, but the marker should respect the bounds.
  @param {number} rate The seeding rate
  @param {number} minSlider The minimum value for the slider
  @param {number} maxSlider The maximum value for the slider
  @returns {number} The marker position
*/
const getMarkerPosition = (rate, minSlider, maxSlider) => {
  if (rate >= minSlider && rate <= maxSlider) {
    return rate;
  }
  if (rate < minSlider) {
    return minSlider;
  }
  else {
    return maxSlider;
  }
};

const PlantingRateInputs = () => {
  const plantingPlanParams = useSelector(getPlantingPlanParams);
  const currency = useSelector(getCurrency);
  const productValidationValues = useSelector(getProductValidationValues);
  const { crop, plantingPlanParams: activeScenarioPlantingPlan } = useSelector(getActiveScenario);
  const cropId = useSelector(getActiveCropId);
  const dispatch = useDispatch();
  const [plantingRate, setPlantingRate] = useState({ 
    value: plantingPlanParams.currentAveragePopulation,
    isInvalid: false
  });

  const { RATE: { max }} = productValidationValues.PLANTING[crop.toUpperCase()];
  const maxSlider = formatNumberRoundUpToNearest(max, 1000);
  const minSlider = formatNumberRoundDownToNearest(maxSlider * RATIOS.MIN_RATE, 1000);
  const marker = getMarkerPosition(Number(plantingPlanParams.currentAveragePopulation), minSlider, maxSlider);
  
  const createScenario = (plantingRate) => {
    if (plantingRate !== activeScenarioPlantingPlan.currentAveragePopulation.toString()) {
      dispatch(loadNewAdvancedScript(plantingRate));
    }
  };
  const createScenarioDebouncedFromSlider = useDebounce(createScenario);
  const createScenarioDebouncedFromInput = useDebounce(createScenario, 1000);
  const onMoveSlider = (newPlantingRate) => {
    const plantingRate = Math.round(newPlantingRate / 100) * 100;
    dispatch(updatePlantingPlanParams({ currentAveragePopulation: plantingRate }));
    createScenarioDebouncedFromSlider(plantingRate);
  };

  const changePlantingRate = (event) => {
    const { value: plantingRate } = event.target;
    const newPlantingPlanParams = { ...plantingPlanParams, currentAveragePopulation: plantingRate };
    const { currentAveragePopulation: errors } = validateAdvancedPlantingPlan({
      cropId,
      currency,
      isDensityAdjustmentEnabled: true,
      plantingPlanParams: newPlantingPlanParams,
      productValidationValues
    });
    
    if (errors) {
      setPlantingRate({ value: plantingRate, isInvalid: true });
      return;
    }

    setPlantingRate({ value: plantingRate, isInvalid: false });
    dispatch(updatePlantingPlanParams({ currentAveragePopulation: plantingRate }));
    createScenarioDebouncedFromInput(plantingRate);
  };

  return (
    <section className='field-average-slider'>
      <TextInput
        value={plantingRate.value}
        onChange={changePlantingRate}
        className={plantingRate.isInvalid && 'isInvalid'}
        error={plantingRate.isInvalid}
        showReset={false}
        showLabel={false}
        testId='current-average-population-input'
      />
      <Slider
        max={maxSlider}
        min={minSlider}
        markers={[marker]}
        handleSize='small'
        onChange={onMoveSlider}
      />
    </section>
  );
};

export default PlantingRateInputs;
